import BaseApi from "apps/visits/lib/BaseApi";
import { createRoute } from "apps/visits/packages/shared/models/Route";
import { HTTPMethods } from "apps/visits/packages/shared/static/http-methods";
import type { BloodTestPackage } from "../models/blood-test-package";
import type { ApiOrder } from "../models/order";
import type { VoucherDistribution } from "../models/voucher-distribution";
import type { AuthenticateUserDTO, AuthenticateUserResponse } from "./authenticate-user/authenticate-user.types";
import type {
  CreateVerificationSessionDTO,
  CreateVerificationSessionResponse,
} from "./create-verification-session/create-verification-session.types";
import type { GetRecommendationsResponse } from "./get-recommendations";
import type {
  PatientAuthenticationDTO,
  PatientAuthenticationResponse,
} from "./patient-authentication/patient-authentication.types";
import type { RegisterPatientDTO, RegisterPatientResponse } from "./register-patient/register-patient.types";
import type {
  ResendVerificationCodeDTO,
  ResendVerificationCodeResponse,
} from "./resend-verification-code/resend-verification-code.types";
import type { ValidateUserDTO, ValidateUserResponse } from "./validate-user/validate-user.types";
import type { VerifyPhoneDTO, VerifyPhoneResponse } from "./verify-phone/verify-phone.types";
import type {
  BasicDTO,
  BloodTestsResponse,
  CreateVoucherDTO,
  GetOrderDTO,
  GetOrderParams,
  PurchaseCatalogProductsResponse,
  SlugParam,
} from "./vouchers-distribution.types";

export const routes = {
  getVoucherDistribution: createRoute<SlugParam>("/api/v2/vouchers_ordering/vouchers_distributions/:slug"),
  getBloodTests: createRoute<SlugParam>("/api/v2/vouchers_ordering/vouchers_distributions/:slug/blood_tests"),
  getProducts: createRoute<SlugParam>("/api/v2/vouchers_ordering/vouchers_distributions/:slug/products"),
  getPackages: createRoute<SlugParam>("/api/v2/vouchers_ordering/vouchers_distributions/:slug/blood_test_packages"),
  validateUser: createRoute<SlugParam>("/api/v2/vouchers_ordering/vouchers_distributions/:slug/user_validations"),
  authenticateUser: createRoute<SlugParam>(
    "/api/v2/vouchers_ordering/vouchers_distributions/:slug/user_authentications",
  ),
  createVoucherOrder: createRoute<SlugParam>("/api/v2/vouchers_ordering/vouchers_distributions/:slug/orders"),
  getOrder: createRoute<GetOrderParams>("/api/v2/vouchers_ordering/vouchers_distributions/:slug/orders/:uuid"),
  resendVerificationCode: createRoute<SlugParam>(
    "/api/v2/vouchers_ordering/vouchers_distributions/:slug/token_refreshes",
  ),
  getRecommendations: createRoute("/api/v2/labplus/recommendations/details"),
  verifyPhone: createRoute<SlugParam>("/api/v2/vouchers_ordering/vouchers_distributions/:slug/phone_verifications"),
  createVerificationSession: createRoute<SlugParam>(
    "/api/v2/vouchers_ordering/vouchers_distributions/:slug/verification_sessions",
  ),
  patientAuthentication: createRoute<SlugParam>(
    "/api/v2/vouchers_ordering/vouchers_distributions/:slug/patient_authentications",
  ),
  registerPatient: createRoute<SlugParam>("/api/v2/vouchers_ordering/vouchers_distributions/:slug/registrations"),
};

export type GetVoucherDistributionResponse = {
  voucherDistribution: VoucherDistribution;
  cmsTranslations: Record<string, Record<string, string>>;
  locale: string;
};

export class VouchersDistributionsRepository extends BaseApi {
  async getVoucherDistribution({ slug }: SlugParam) {
    return this.request<GetVoucherDistributionResponse>({
      url: routes.getVoucherDistribution.apply({ slug }),
      method: HTTPMethods.GET,
    });
  }

  async getBloodTests({ slug, ...params }: BasicDTO) {
    return this.request<BloodTestsResponse>({
      url: routes.getBloodTests.apply({ slug }),
      method: HTTPMethods.GET,
      params,
    });
  }

  async getProducts({ slug, ...params }: BasicDTO) {
    return this.request<PurchaseCatalogProductsResponse>({
      url: routes.getProducts.apply({ slug }),
      method: HTTPMethods.GET,
      params,
    });
  }
  async validateUser({ slug, ...data }: ValidateUserDTO) {
    return this.request<ValidateUserResponse>({
      url: routes.validateUser.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }

  async getPackages({ slug, ...params }: BasicDTO) {
    return this.request<{ bloodTestPackages: BloodTestPackage[] }>({
      url: routes.getPackages.apply({ slug }),
      method: HTTPMethods.GET,
      params,
    });
  }

  async createVoucherOrder({ slug, ...data }: CreateVoucherDTO) {
    return this.request<{ order: ApiOrder }>({
      url: routes.createVoucherOrder.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }

  async getOrder({ slug, uuid, ...params }: GetOrderDTO) {
    return this.request<{ order: ApiOrder }>({
      url: routes.getOrder.apply({ slug, uuid }),
      method: HTTPMethods.GET,
      params,
    });
  }
  async authenticateUser({ slug, ...data }: AuthenticateUserDTO) {
    return this.request<AuthenticateUserResponse>({
      url: routes.authenticateUser.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }
  async resendVerificationCode({ slug, ...data }: ResendVerificationCodeDTO) {
    return this.request<ResendVerificationCodeResponse>({
      url: routes.resendVerificationCode.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }
  async getRecommendations() {
    return this.request<GetRecommendationsResponse>({
      url: routes.getRecommendations.raw(),
      method: HTTPMethods.GET,
    });
  }
  async verifyPhone({ slug, ...data }: VerifyPhoneDTO) {
    return this.request<VerifyPhoneResponse>({
      url: routes.verifyPhone.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }
  async createVerificationSession({ slug, ...data }: CreateVerificationSessionDTO) {
    return this.request<CreateVerificationSessionResponse>({
      url: routes.createVerificationSession.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }
  async patientAuthentication({ slug, ...data }: PatientAuthenticationDTO) {
    return this.request<PatientAuthenticationResponse>({
      url: routes.patientAuthentication.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }
  async registerPatient({ slug, ...data }: RegisterPatientDTO) {
    return this.request<RegisterPatientResponse>({
      url: routes.registerPatient.apply({ slug }),
      method: HTTPMethods.POST,
      data,
    });
  }
}
